import { detect_site_url } from "@core/helpers/api-headers";
import { get_cookie } from "@core/helpers/cookie";
import { is_mobile_device } from "@core/helpers/device";
import { get_current_locate_from_cookie } from "@core/helpers/locate";
import { get_dev_mode, dev_mode_headers } from "@core/helpers/dev-mode";
import { dev_mode_ended_path } from "@core/helpers/dev-mode-rules";

/*
 * The files of a form builder form, sent to the web-server before the answer (POST /api/form-files/upload, multipart).
 * Added for jument (useFormRenderer's `upload_files` option): the answer then carries { upload_id, upload_token } instead
 * of the files themselves in base64. Browser only.
 *
 *   upload_form_files({ form_key | form_definition_id, version, captcha, files: [{ path, blob, file_name }] })
 *     -> { status: true, data: { files: [{ path, upload_id, upload_token, name, size }] } }
 *      | { status: false, errors: [{ message, errors, code }] }   (the shape of a GraphQL error, so the renderer reads it the same way)
 * Never throws.
 */

// the REST api next to the GraphQL one: ".../api/graphql" -> ".../api/form-files/upload"
export const form_files_upload_url = (graphql_url = process.env.NEXT_PUBLIC_GRAPHQL_URL || process.env.GRAPHQL_URL) => {
    const base = String(graphql_url || "").replace(/\/graphql\/?$/, "");
    return base ? `${base}/form-files/upload` : "";
};

export const upload_form_files = async ({ form_key, form_definition_id, version, captcha, files = [] } = {}) => {
    // storefront dev mode (documents/helpers/devMode.md): the files go to the API the developer chose, with the token, so
    // the replica stores them where its createFormSubmission finds them, as Apollo does for the answer
    const dev_mode = await get_dev_mode();
    const url = form_files_upload_url(dev_mode?.api_url || undefined);
    if (!url || typeof FormData === "undefined" || typeof fetch === "undefined") {
        return { status: false, errors: undefined };
    }
    try {
        const body = new FormData();
        if (form_definition_id) {
            body.append("form_definition_id", String(form_definition_id));
        } else if (form_key) {
            body.append("form_key", String(form_key));
        }
        if (Number.isInteger(version)) {
            body.append("version", String(version));
        }
        if (captcha) {
            body.append("captcha", String(captcha));
        }
        files.forEach(({ path, blob, file_name }) => body.append(path, blob, file_name));

        const token = await get_cookie("_token");
        const headers = {
            "site-url": await detect_site_url(),
            "device-type": (await is_mobile_device()) ? "mobile" : "desktop",
            lang: await get_current_locate_from_cookie(),
            ...dev_mode_headers(dev_mode),
        };
        if (token) {
            headers.authorization = `Bearer ${token}`;
        }

        const response = await fetch(url, { method: "POST", headers, body });
        const json = await response.json().catch(() => null);
        if (response.ok && json?.success) {
            return { status: true, data: json.data };
        }
        // the developer's token no longer opens the replica: leave dev mode as graphql.js does
        if (json?.message === "dev_mode_expired" && typeof window !== "undefined") {
            window.location.assign(dev_mode_ended_path(json?.errors?.dev_mode?.[0], window.location.pathname + window.location.search));
        }
        return { status: false, errors: [{ message: json?.message, errors: json?.errors, code: String(response.status) }] };
    } catch {
        return { status: false, errors: undefined };
    }
};
